import { useState, useMemo, useCallback, useEffect, type FC } from "react";
import "../styles/EmailList.css";
import EmailCard from "./EmailCard";
import type { IconType } from "react-icons";
import { MdFolder, MdCloudQueue, MdOutlineCreateNewFolder, MdSettings, MdCommentsDisabled, MdSync, MdClose } from "react-icons/md";
import { EmailData } from "../../../../common/src/types/types";
import { deleteEmail, getClientConfig, getFolderEmails, removeEmailBody, saveEmailsToFolder, searchEmails, syncLegacyFolder, updateFolderBodyStoragePreference, updateFolderKeywords } from "../../apiClient";
import { getFolderURL } from "../../graphClient";
import { useAppContext } from "../contexts/AppContext";
import FavoriteRenameModal from "./FavoriteRenameModal";
import ContextMenu, { type ContextMenuItem } from "./ContextMenu";

type SortKey = "date" | "sender" | "recipient" | "subject";
type SortOrder = "asc" | "desc";
type ArchiveFolderType = "project" | "onedrive" | "custom";
const SORT_LABELS: Record<SortKey, string> = {
    date: "Date",
    sender: "Sender",
    recipient: "Recipient",
    subject: "Subject",
};

const EmailList: FC = () => {
    const { openedProject, navigateToFolders, favorites, navigateToSave, quickSaveEmails, refreshFavorites, archiveStatusVersion, openEmailPreviewDialog } = useAppContext();
    const projectId = openedProject!;
    const folderInfo = favorites.find(f => f.id === projectId);
    const [isDragOver, setIsDragOver] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortKey, setSortKey] = useState<SortKey>("date");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const [selectedEmail, setSelectedEmail] = useState<EmailData | null>(null);
    const [emails, setEmails] = useState<EmailData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
    const [showKeywordModal, setShowKeywordModal] = useState(false);
    const [editingKeywords, setEditingKeywords] = useState<string[]>([]);
    const [newKeywordInput, setNewKeywordInput] = useState<string>("");
    const [doNotStoreBody, setDoNotStoreBody] = useState(folderInfo?.doNotStoreBody ?? false);
    const [folderUrl, setFolderUrl] = useState<string | null>(null);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [projectRootPath, setProjectRootPath] = useState("");
    const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number; email: EmailData | null }>({
        visible: false, x: 0, y: 0, email: null,
    });

    const currentUserEmail = Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase() || "";

    const folderTypeLabels: Record<ArchiveFolderType, { label: string; className: string; Icon: IconType }> = {
        project: { label: "Project Archive", className: "project", Icon: MdFolder },
        onedrive: { label: "Private OneDrive", className: "onedrive", Icon: MdCloudQueue },
        custom: { label: "Custom Favorite", className: "custom", Icon: MdOutlineCreateNewFolder },
    };

    const mappedType: ArchiveFolderType = folderInfo?.favoriteType === "onedrive" ? "onedrive" : folderInfo?.favoriteType === "custom" ? "custom" : "project";
    const typeLabel = folderTypeLabels[mappedType];
    const allowBodyStorageToggle = mappedType === "onedrive" || mappedType === "custom";
    const handleRemoveEmailBody = useCallback(async (emailId: string) => {
        try {
            await removeEmailBody(emailId);
            setEmails((prev) =>
                prev.map((email) =>
                    email.id === emailId
                        ? { ...email, body: "", bodyRemoved: true }
                        : email
                )
            );
        } catch (err) {
            console.error("Failed to remove email body", err);
        }
    }, []);

    const handleEditKeywords = () => {
        const currentKeywords = folderInfo?.keywords || [];
        setEditingKeywords([...currentKeywords]);
        setShowKeywordModal(true);
        setShowSettingsDropdown(false);
    };

    const handleSaveKeywords = async () => {
        if (!folderInfo?.id) return;

        try {
            await updateFolderKeywords(folderInfo.id, editingKeywords);
            await refreshFavorites();
            setShowKeywordModal(false);
            setEditingKeywords([]);
            setNewKeywordInput("");
        } catch (error) {
            console.error('Failed to save keywords:', error);
        }
    };

    const handleCancelKeywordEdit = () => {
        setShowKeywordModal(false);
        setEditingKeywords([]);
        setNewKeywordInput("");
    };

    const handleAddKeyword = () => {
        const keyword = newKeywordInput.trim();
        if (keyword && !editingKeywords.includes(keyword)) {
            setEditingKeywords(prev => [...prev, keyword]);
            setNewKeywordInput("");
        }
    };

    const handleRemoveKeyword = (keywordToRemove: string) => {
        setEditingKeywords(prev => prev.filter(k => k !== keywordToRemove));
    };

    const handleKeywordInputKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddKeyword();
        }
    };

    const onBodyStorageToggle = async (enabled: boolean) => {
        if (!folderInfo?.id) return;
        try {
            await updateFolderBodyStoragePreference(folderInfo.id, enabled);
            setDoNotStoreBody(enabled);
            setShowSettingsDropdown(false);
            await refreshFavorites();
        } catch (error) {
            console.error("Failed to update body storage preference:", error);
        }
    };

    const handleLegacySync = async () => {
        if (!folderInfo?.id) return;
        try {
            await syncLegacyFolder(folderInfo.id);
            setShowSettingsDropdown(false);
            await refreshFavorites();
        } catch (error) {
            console.error("Failed to queue legacy folder sync:", error);
        }
    };

    const handleEmailSelection = (email: EmailData) => {
        setSelectedEmail(email);
        void openEmailPreviewDialog(email, setSelectedEmail, handleRemoveEmailBody);
    };

    const handleEmailContextMenu = (e: React.MouseEvent, email: EmailData) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ visible: true, x: e.clientX, y: e.clientY, email });
    };

    const closeContextMenu = () => {
        setContextMenu({ visible: false, x: 0, y: 0, email: null });
    };

    const handleDeleteEmail = async (emailId: string) => {
        closeContextMenu();
        try {
            await deleteEmail(emailId);
            setEmails((prev) => prev.filter((email) => email.id !== emailId));
        } catch (err) {
            console.error("Failed to delete email", err);
        }
    };

    const handleContextRemoveBody = async (emailId: string) => {
        closeContextMenu();
        await handleRemoveEmailBody(emailId);
    };

    const handleRetryArchive = async (email: EmailData) => {
        closeContextMenu();
        try {
            await saveEmailsToFolder(folderInfo!.id!, [email], undefined, true);
            setEmails((prev) =>
                prev.map((e) =>
                    e.id === email.id
                        ? { ...e, archiveStatus: "Queued" }
                        : e
                )
            );
        } catch (err) {
            console.error("Failed to retry archive", err);
        }
    };

    const handleCopyPath = async () => {
        closeContextMenu();
        try {
            if (folderInfo?.folderPath) {
                let folderPath = folderInfo.favoriteType === "project" ? projectRootPath + folderInfo.folderPath : folderInfo.folderPath;
                folderPath = folderPath + "\\"; //add file path to the folder path
                await navigator.clipboard.writeText(folderPath);
            } else {
                console.warn("Folder path not available for folder:", folderInfo?.id);
            }
        } catch (error) {
            console.error("Failed to copy folder path:", error);
        }
    };

    const handleCopyEmailFilePath = async (email: EmailData) => {
        closeContextMenu();
        if (!email.filePath) {
            return;
        }

        try {
            await navigator.clipboard.writeText(email.filePath);
        } catch (error) {
            console.error("Failed to copy email file path:", error);
        }
    };

    useEffect(() => {
        getClientConfig()
            .then((config) => setProjectRootPath(config.projectRootPath))
            .catch((err) => console.error("Failed to load client config", err));
    }, []);

    useEffect(() => {
        if (folderInfo?.favoriteType === "onedrive" && folderInfo.folderPath) {
            getFolderURL(folderInfo.folderPath).then(url => setFolderUrl(url));
        }
    }, [folderInfo]);

    // Close settings dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (showSettingsDropdown) {
                setShowSettingsDropdown(false);
            }
        };

        if (showSettingsDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showSettingsDropdown]);

    // Debounced search via API
    useEffect(() => {
        if (!searchTerm.trim()) {
            setEmails((prev) => prev); // no-op; initial load handles empty state
            return;
        }

        const timer = setTimeout(async () => {
            try {
                setIsLoading(true);
                const results = await searchEmails(searchTerm, projectId);
                const normalized = (results || []).map((email) => ({
                    ...email,
                    date: email.date instanceof Date ? email.date : new Date(email.date),
                    bodyRemoved: email.bodyRemoved ?? false,
                    allowBodyRemoval: email.allowBodyRemoval ?? true,
                    body: email.body ?? "",
                }));
                setEmails(normalized);
            } catch (err) {
                console.error("Failed to search emails", err);
            } finally {
                setIsLoading(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Re-load folder emails when search is cleared
    useEffect(() => {
        if (searchTerm.trim()) return;

        let isActive = true;
        const reload = async () => {
            try {
                setIsLoading(true);
                const response = await getFolderEmails(projectId);
                if (!isActive) return;
                const normalized = (response || []).map((email) => ({
                    ...email,
                    date: email.date instanceof Date ? email.date : new Date(email.date),
                    bodyRemoved: email.bodyRemoved ?? false,
                    allowBodyRemoval: email.allowBodyRemoval ?? true,
                    body: email.body ?? "",
                }));
                setEmails(normalized);
            } catch (err) {
                console.error("Failed to reload emails", err);
            } finally {
                if (isActive) setIsLoading(false);
            }
        };
        reload();
        return () => { isActive = false; };
    }, [searchTerm, projectId, archiveStatusVersion]);

    const sortedEmails = useMemo(() => {
        const sorted = [...emails];
        sorted.sort((a, b) => {
            let aValue: any = a[sortKey];
            let bValue: any = b[sortKey];
            if (sortKey === "date") {
                aValue = aValue.getTime();
                bValue = bValue.getTime();
            } else {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }
            if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
            if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [emails, sortKey, sortOrder]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortOrder("asc");
        }
    };

    const formatCardDate = (date: Date) => {
        const datePart = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
        const timePart = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        return `${datePart} • ${timePart}`;
    };

    const titleRegex = new RegExp(String.raw`(${folderInfo?.projectNumber}\s?-\s?)`, "g");

    return (
        <div
            className={`email-list-container${isDragOver ? " drag-over" : ""}`}
            onDragEnter={(e) => {
                e.preventDefault();
                setIsDragOver(true);
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setIsDragOver(false);
                }
            }}
            onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
                const saved = await quickSaveEmails(projectId);
                if (!saved) {
                    navigateToSave();
                }
            }}
        >
            <div className="email-header">
                <div className="email-header-top">
                    <button className="back-button" onClick={navigateToFolders}>
                        ← Back
                    </button>
                    <div className="header-right">
                        <span
                            className={`archive-type-badge ${typeLabel.className}${folderUrl ? " clickable" : ""}`}
                            aria-label={`Archive folder type: ${typeLabel.label}`}
                            onClick={folderUrl ? () => window.open(folderUrl, "_blank") : undefined}
                            title={folderUrl ? 'Open in OneDrive' : folderInfo?.folderPath ? "Path: " + folderInfo.folderPath : undefined}
                        >
                            <span className="archive-type-icon" aria-hidden="true">
                                <typeLabel.Icon />
                            </span>
                            <span className="archive-type-text">{typeLabel.label}</span>
                        </span>
                        {doNotStoreBody && (
                            <span className="body-storage-warning" title="Email bodies are not stored for this folder." aria-label="Email bodies are not stored for this folder">
                                <MdCommentsDisabled size={16} />
                            </span>
                        )
                        }
                        <div className="settings-dropdown-container">
                            <button
                                className="settings-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSettingsDropdown(!showSettingsDropdown);
                                }}
                                title="Settings"
                            >
                                <MdSettings />
                            </button>
                            {showSettingsDropdown && (
                                <div className="settings-dropdown" onClick={(e) => e.stopPropagation()}>
                                    <div
                                        className="settings-dropdown-item"
                                        onClick={handleEditKeywords}
                                    >
                                        Edit Keywords
                                    </div>
                                    <div
                                        className="settings-dropdown-item"
                                        onClick={() => {
                                            setShowRenameModal(true);
                                            setShowSettingsDropdown(false);
                                        }}
                                    >
                                        Rename Favorite
                                    </div>
                                    <div
                                        className="settings-dropdown-item"
                                        onClick={handleLegacySync}
                                    >
                                        {folderInfo?.importLegacy ? 'Legacy Emails Queued for Import' : 'Sync Legacy Emails'}
                                    </div>
                                    {allowBodyStorageToggle && (
                                        <div
                                            className="settings-dropdown-item"
                                            onClick={() => {
                                                onBodyStorageToggle(!doNotStoreBody);
                                                setShowSettingsDropdown(false);
                                            }}
                                        >
                                            {doNotStoreBody ? 'Enable body storage' : 'Disable body storage'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {folderInfo?.importLegacy && (
                            <div className="settings-dropdown-container">
                                <button
                                    className="settings-button cursor-none"
                                    onClick={(e) => {
                                        e.stopPropagation();

                                    }}
                                    title="Syncing Legacy Emails"
                                >
                                    <MdSync />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <h2 className="email-header-name" onClick={handleCopyPath} title={folderInfo?.favoriteType === "onedrive" ? "" : "Path: " + folderInfo?.folderPath + "\nClick to copy"}>{folderInfo?.name.replace(titleRegex, "")}</h2>
                {folderInfo?.projectNumber && <h5 className="email-header-projectnumber">#{folderInfo?.projectNumber}</h5>}
            </div>

            <div className="email-search">
                <div className="search-input-wrapper">
                    <input
                        type="text"
                        placeholder="Search emails by sender, recipient, subject, or body..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="email-search-input"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="search-clear-button"
                            aria-label="Clear search"
                            title="Clear search"
                            onClick={() => setSearchTerm("")}
                        >
                            <MdClose />
                        </button>
                    )}
                </div>
            </div>

            <div className="email-toolbar">
                <div className="sort-pills" role="group" aria-label="Sort emails">
                    {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => {
                        const isActive = sortKey === key;
                        return (
                            <button key={key} type="button" className={isActive ? "sort-pill active" : "sort-pill"} onClick={() => handleSort(key)}>
                                {SORT_LABELS[key]}
                                {isActive && <span className="sort-pill-indicator">{sortOrder === "asc" ? "↑" : "↓"}</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="email-card-scroll">
                {isLoading && <div className="email-empty-state">Loading emails...</div>}
                {error && !isLoading && <div className="email-empty-state">{error}</div>}
                {!isLoading && !error && sortedEmails.length ? (
                    <div className="email-card-list">
                        {sortedEmails.map((email) => (
                            <div key={email.id} onContextMenu={(e) => handleEmailContextMenu(e, email)}>
                                <EmailCard email={email} selected={selectedEmail?.id === email.id} onSelect={handleEmailSelection} formatDate={formatCardDate} />
                            </div>
                        ))}
                    </div>
                ) : null}
                {!isLoading && !error && !sortedEmails.length && (
                    <div className="email-empty-state">No emails match your search.</div>
                )}
            </div>

            {showKeywordModal && (
                <div className="keyword-edit-modal-overlay" onClick={handleCancelKeywordEdit}>
                    <div className="keyword-edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="keyword-edit-header">
                            <h3>Edit Keywords</h3>
                            <button className="keyword-edit-close" onClick={handleCancelKeywordEdit}>
                                ×
                            </button>
                        </div>
                        <div className="keyword-edit-body">
                            <label>Keywords:</label>
                            <div className="keyword-list">
                                {editingKeywords.map((keyword, index) => (
                                    <div key={index} className="keyword-item">
                                        <span className="keyword-text">{keyword}</span>
                                        <button
                                            className="keyword-remove-btn"
                                            onClick={() => handleRemoveKeyword(keyword)}
                                            title="Remove keyword"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                {editingKeywords.length === 0 && (
                                    <div className="keyword-empty">No keywords added yet</div>
                                )}
                            </div>
                            <div className="keyword-add-section">
                                <input
                                    type="text"
                                    className="keyword-add-input"
                                    value={newKeywordInput}
                                    onChange={(e) => setNewKeywordInput(e.target.value)}
                                    onKeyPress={handleKeywordInputKeyPress}
                                    placeholder="Enter a keyword..."
                                />
                                <button
                                    className="keyword-add-btn"
                                    onClick={handleAddKeyword}
                                    disabled={!newKeywordInput.trim() || editingKeywords.includes(newKeywordInput.trim())}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                        <div className="keyword-edit-footer">
                            <button className="keyword-edit-cancel" onClick={handleCancelKeywordEdit}>
                                Cancel
                            </button>
                            <button className="keyword-edit-save" onClick={handleSaveKeywords}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <FavoriteRenameModal
                visible={showRenameModal}
                folderId={folderInfo?.id || null}
                currentName={folderInfo?.name || ""}
                onClose={() => setShowRenameModal(false)}
                onUpdate={(newName) => {
                    refreshFavorites();
                    setShowRenameModal(false);
                }}
            />

            {(() => {
                const email = contextMenu.email;
                const canDelete = email?.archiveUserId?.toLowerCase() === currentUserEmail;
                const canRemoveBody = !!email?.allowBodyRemoval && !email.bodyRemoved;
                const items: ContextMenuItem[] = email
                    ? [
                        {
                            key: "remove",
                            label: "Remove from E-File",
                            disabled: !canDelete,
                            title: !canDelete
                                ? "You can only remove emails you archived"
                                : "Removes email from the E-File Database and Queue if it hasn't been archived yet; the email will still exist in the actual folder.",
                            onClick: () => handleDeleteEmail(email.id),
                        },
                        {
                            key: "remove-body",
                            label: "Remove Email Body",
                            disabled: !canRemoveBody,
                            title: !canRemoveBody ? "Body removal is not available for emails in shared project folders" : undefined,
                            onClick: () => handleContextRemoveBody(email.id),
                        },
                        ...(email.archiveStatus === "Failed" ? [
                            {
                                key: "retry",
                                label: "Retry Archiving",
                                title: "Attempt to archive this email again",
                                onClick: () => handleRetryArchive(email),
                            }
                        ] : []),
                        {
                            key: "copy-path",
                            label: "Copy File Path",
                            disabled: !email.filePath,
                            title: email.filePath
                                ? "Copies the archived email file path to your clipboard"
                                : "No archived file path is available for this email",
                            onClick: () => handleCopyEmailFilePath(email),
                        },
                    ]
                    : [];
                return (
                    <ContextMenu
                        visible={contextMenu.visible && !!email}
                        x={contextMenu.x}
                        y={contextMenu.y}
                        items={items}
                        onClose={closeContextMenu}
                    />
                );
            })()}
        </div>
    );
};

export default EmailList;
